import { useIsAuthenticated } from '@heidi/auth/state';
import type { Shared__enums__enums__SortOrder as SortOrderType } from '@heidi/schema/ml-scribe';
import {
  type TemplateSortBy,
  type TemplateType,
  type TemplateVisibility,
} from '@heidi/schema/ml-scribe';
import { useQuery } from '@tanstack/react-query';
import {
  GET_ORGANIZATION_TEMPLATES,
  type SearchTemplatesResponse,
} from '../../contracts';
import { getOrganizationTemplates } from '../api/templates';

export interface OrganizationTemplatesQueryInput {
  organizationId?: string;
  keyword?: string;
  page?: number;
  pageSize?: number;
  sortBy?: TemplateSortBy;
  order?: SortOrderType;
  types?: TemplateType[];
  creatorIds?: string[];
  visibilities?: TemplateVisibility[];
  sharedTeamIds?: string[];
}

export function useQueryOrganizationTemplates<T = SearchTemplatesResponse>(
  input?: OrganizationTemplatesQueryInput,
  select?: (data: SearchTemplatesResponse) => T,
  /** Caller-supplied user gate. Authentication is read here. */
  isEnabled = true
) {
  const { isAuthenticated } = useIsAuthenticated();

  return useQuery({
    queryKey: [
      GET_ORGANIZATION_TEMPLATES,
      { organizationId: input?.organizationId, input },
    ],
    retry: 3,
    enabled: isAuthenticated && isEnabled && Boolean(input?.organizationId),
    queryFn: async () => {
      const { data } = await getOrganizationTemplates({
        keyword: input?.keyword,
        sortBy: input?.sortBy,
        order: input?.order ?? ('ASC' as SortOrderType),
        page: input?.page,
        pageSize: input?.pageSize,
        types: input?.types,
        creatorIds: input?.creatorIds,
        visibilities: input?.visibilities,
        organizationId: input?.organizationId ?? '',
        sharedTeamIds: input?.sharedTeamIds,
      });
      return data;
    },
    select,
  });
}
